"use client";

import { useState, type FormEvent } from "react";
import { ArrowRight, MapPin } from "@/components/icons";
import { ErrorBoundary } from "@/components/error/error-boundary";
import { Button } from "@/components/ui/button";
import { HoverPrefetchLink } from "@/components/ui/hover-prefetch-link";
import { Input } from "@/components/ui/input";
import { PUBLIC_PAGE_SHELL_CLASSES } from "@/components/layout/surface-page";
import { useLocationContext, type HeroLocation } from "@/components/home/context/location-context";
import { buildListingSearchQuery } from "@/components/listing/utils/parse-search-params";
import type { DiscoveryProduct } from "@/components/discovery/types";
import { ListingsClusterMapClient } from "@/components/location/listings-cluster-map-client";
import { previewViewport, useClusterListings } from "@/components/listing/browse/listing-map-pane";
import { NEAR_YOU_REACH_MILES, useNearYouProducts } from "@/components/home/sections/near-you/use-near-you-products";
import { useVisitorLocation } from "@/components/home/sections/near-you/use-visitor-location";
import { geocodePostalCode } from "@/lib/geocoding/geocode";
import { formatCentsWithCurrency } from "@/lib/formatting/currency";
import { ProductTypeIcon } from "@/lib/icons/product-icons";
import { US_MAP_CENTER } from "@/lib/maps/map-defaults";
import { cn } from "@/lib/utils";
import { DISPLAY_HEADING_CLASSES } from "./display-font";
import { listingItem } from "./landing-listing-item";
import { LANDING_PRODUCTS, type LandingProductSlug } from "./landing-product-picker";

const NATIONWIDE_ZOOM = 3;
const SHOWN_ROWS = 6;
const SKELETON_ROWS = 5;
const ZIP_PATTERN = /^\d{5}$/;

function lowestPriceCents(products: DiscoveryProduct[]): number | null {
  const prices = products
    .map((product) => product.buyerUnitPriceCents)
    .filter((cents): cents is number => typeof cents === "number" && cents > 0);
  return prices.length > 0 ? Math.min(...prices) : null;
}

function placeName(location: HeroLocation | null): string | null {
  if (location?.city && location.state) return `${location.city}, ${location.state}`;
  return location?.postalCode ?? null;
}

function ProductFilter({
  value,
  onChange,
}: {
  value: LandingProductSlug | undefined;
  onChange: (slug: LandingProductSlug | undefined) => void;
}) {
  const options = [{ slug: undefined, label: "All packaging" }, ...LANDING_PRODUCTS];
  return (
    <div role="group" aria-label="Product" className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
      <div className="flex w-max gap-2">
        {options.map((option) => {
          const isActive = option.slug === value;
          return (
            <button
              key={option.label}
              type="button"
              aria-pressed={isActive}
              onClick={() => onChange(option.slug)}
              className={cn(
                "flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-semibold transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring",
                isActive ? "bg-foreground text-background" : "bg-surface text-foreground ring-1 ring-border hover:bg-muted/50",
              )}
            >
              {option.slug ? <ProductTypeIcon slug={option.slug} size={20} className="size-5" /> : null}
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ZipForm() {
  const { location, setLocation } = useLocationContext();
  const [zip, setZip] = useState("");
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!ZIP_PATTERN.test(zip)) {
      setError("Enter a 5-digit ZIP.");
      return;
    }
    const geo = await geocodePostalCode(zip).catch(() => null);
    if (!geo) {
      setError("We couldn't find that ZIP.");
      return;
    }
    setLocation({ lat: geo.latitude, lng: geo.longitude, postalCode: zip, city: geo.city, state: geo.state });
    setZip("");
  };
  const place = placeName(location);
  return (
    <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1 rounded-2xl border border-border bg-surface p-1.5 pl-4 shadow-sm focus-within:border-brand">
        <MapPin aria-hidden className="size-4 shrink-0 text-brand-strong" />
        <Input
          variant="ghost"
          inputMode="numeric"
          autoComplete="postal-code"
          aria-label="Delivery ZIP"
          placeholder={place ?? "Delivery ZIP"}
          value={zip}
          onChange={(event) => setZip(event.target.value.replace(/\D/g, "").slice(0, 5))}
          className="h-10 min-w-0 flex-1 sm:w-56 sm:flex-none"
        />
        <Button type="submit" variant="ghost" className="h-10 font-semibold">
          Set ZIP
        </Button>
      </div>
      {error ? (
        <p role="alert" className="text-sm text-destructive-strong">
          {error}
        </p>
      ) : null}
    </form>
  );
}

function BoardStat({ products, isLoading, scope }: { products: DiscoveryProduct[]; isLoading: boolean; scope: string }) {
  const lowest = lowestPriceCents(products);
  return (
    <div className="flex flex-col gap-1 border-b border-border-subtle p-6 sm:p-8">
      <span className="text-[13px] font-semibold text-fg-muted">Lowest listed price</span>
      <span className={cn(DISPLAY_HEADING_CLASSES, "text-[44px] leading-none tabular-nums sm:text-[60px]")}>
        {!isLoading && lowest ? formatCentsWithCurrency(lowest) : "—"}
        <span className="ml-2 font-sans text-base font-medium tracking-normal text-fg-muted">per unit</span>
      </span>
      <span className="pt-2 text-[15px] text-fg-muted">
        {isLoading ? "Reading the market…" : products.length === 0 ? `No listings ${scope} right now` : `${products.length} listing${products.length === 1 ? "" : "s"} ${scope}`}
      </span>
    </div>
  );
}

function BoardRows({ products, isLoading }: { products: DiscoveryProduct[]; isLoading: boolean }) {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 p-6 sm:px-8">
        {Array.from({ length: SKELETON_ROWS }, (_, index) => (
          <div key={index} aria-hidden className="h-12 animate-pulse rounded-lg bg-skeleton motion-reduce:animate-none" />
        ))}
      </div>
    );
  }
  if (products.length === 0) {
    return <p className="p-8 text-[15px] text-fg-muted">Nothing listed here yet. Try another product or ZIP.</p>;
  }
  return (
    <ul className="divide-y divide-border-subtle">
      {products.slice(0, SHOWN_ROWS).map((product) => {
        const item = listingItem(product);
        return (
          <li key={product._id}>
            <HoverPrefetchLink
              href={item.href}
              className="group grid grid-cols-[44px_minmax(0,1fr)_auto] items-center gap-4 px-6 py-3 outline-none transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:px-8"
            >
              <span aria-hidden className="flex size-11 items-center justify-center rounded-xl bg-brand-tint">
                <ProductTypeIcon slug={item.productTypeSlug ?? "pallets"} size={26} className="size-[26px]" />
              </span>
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-[15px] font-semibold group-hover:underline">{item.name}</span>
                <span className="truncate text-[13px] text-fg-muted">{item.location.split(" · ")[0]}</span>
              </span>
              <span className="flex flex-col items-end">
                <span className="text-[17px] font-semibold tabular-nums">
                  {item.unitPrice ? `$${item.unitPrice}` : (item.freeLabel ?? "Quote")}
                </span>
                {item.unitPrice ? <span className="text-xs text-fg-muted">per unit</span> : null}
              </span>
            </HoverPrefetchLink>
          </li>
        );
      })}
    </ul>
  );
}

function BoardMap({ products, isLoading }: { products: DiscoveryProduct[]; isLoading: boolean }) {
  const listings = useClusterListings(products);
  const viewport = previewViewport(listings);
  const frame = "h-[320px] w-full lg:h-full lg:min-h-[600px]";
  if (isLoading) return <div aria-hidden className={cn(frame, "animate-pulse bg-skeleton motion-reduce:animate-none")} />;
  return (
    <ListingsClusterMapClient
      listings={listings}
      className={frame}
      mapClassName="h-full w-full rounded-none border-0"
      defaultZoom={viewport?.zoom ?? NATIONWIDE_ZOOM}
      defaultCenter={viewport?.center ?? { latitude: US_MAP_CENTER.lat, longitude: US_MAP_CENTER.lng }}
      emptyMessage="No listings here yet."
    />
  );
}

function MarketHeader() {
  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
      <div className="flex max-w-[640px] flex-col gap-3">
        <h2
          id="landing-market"
          className={cn(DISPLAY_HEADING_CLASSES, "text-[30px] leading-[1.06] sm:text-[48px] sm:leading-[1.03]")}
        >
          The market, live
        </h2>
        <p className="text-base leading-[1.6] text-fg-muted sm:text-lg">
          Real listings from yards across the US, priced per unit. Pick a product and set your ZIP.
        </p>
      </div>
      <ZipForm />
    </div>
  );
}

function MarketBody() {
  const [product, setProduct] = useState<LandingProductSlug | undefined>(undefined);
  const { location, isSeeding } = useVisitorLocation();
  const { products, isLoading, isNationwide } = useNearYouProducts({ location, isSeeding, productTypeSlug: product });
  const reach = isNationwide ? undefined : (location ?? undefined);
  const browseQuery = buildListingSearchQuery({
    productType: product,
    location: reach,
    radiusMiles: reach ? NEAR_YOU_REACH_MILES : undefined,
  });
  const productLabel = LANDING_PRODUCTS.find((candidate) => candidate.slug === product)?.label.toLowerCase();
  const place = placeName(location);
  const scope = isNationwide || !place ? "across the US" : `within ${NEAR_YOU_REACH_MILES} miles of ${place}`;
  return (
    <>
      <div className={cn(PUBLIC_PAGE_SHELL_CLASSES, "flex flex-col gap-6 pt-16 sm:gap-8 sm:pt-24")}>
        <MarketHeader />
        <ProductFilter value={product} onChange={setProduct} />
        <div className="grid overflow-hidden rounded-3xl border border-border bg-surface shadow-2xl shadow-foreground/10 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
          <div className="flex flex-col">
            <BoardStat products={products} isLoading={isLoading} scope={scope} />
            <div className="flex-1">
              <BoardRows products={products} isLoading={isLoading} />
            </div>
            <div className="border-t border-border-subtle p-6 sm:p-8">
              <Button asChild variant="outline" className="h-12 w-full text-[15px]">
                <HoverPrefetchLink href={browseQuery ? `/listing?${browseQuery}` : "/listing"}>
                  {productLabel ? `Shop all ${productLabel}` : "Shop every listing"}
                  <ArrowRight aria-hidden />
                </HoverPrefetchLink>
              </Button>
            </div>
          </div>
          <div className="border-border-subtle max-lg:order-first max-lg:border-b lg:border-l">
            <BoardMap products={products} isLoading={isLoading} />
          </div>
        </div>
      </div>
    </>
  );
}

export function LandingMarket() {
  return (
    <section aria-labelledby="landing-market" className="bg-surface pb-16 sm:pb-28">
      <ErrorBoundary
        label="home-market"
        fallback={
          <div className={cn(PUBLIC_PAGE_SHELL_CLASSES, "pt-16")}>
            <h2 id="landing-market" className="text-2xl font-semibold">
              The market, live
            </h2>
            <p className="text-[15px] text-fg-muted">Listings are unavailable right now.</p>
          </div>
        }
      >
        <MarketBody />
      </ErrorBoundary>
    </section>
  );
}
